import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import { Context } from "../../Reducers/Store";
import Loading from "../../components/Loading";

const emptyUser = {
  nome: "",
  email: "",
  tipoUsuario: 3,
  grupoId: "",
  permissoesExtrasIds: [],
};

export default function UsuarioForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const isEdit = !!id;

  const [aba, setAba] = useState("dados");
  const [form, setForm] = useState(emptyUser);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [grupos, setGrupos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [permissoesGrupo, setPermissoesGrupo] = useState([]);

  const isSistema = state?.profile?.tipo_usuario === "Sistema";

  useEffect(() => {
    if (isSistema && !isEdit) {
      setForm((prev) => ({ ...prev, tipoUsuario: 1 }));
    }
  }, [isSistema, isEdit]);

  // Carrega grupos e permissões disponíveis
  useEffect(() => {
    let cancelled = false;

    async function carregar() {
      try {
        const [gruposRes, permissoesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}/grupos`),
          axios.get(`${process.env.REACT_APP_API}/permissoes`),
        ]);

        if (cancelled) return;

        setGrupos(gruposRes.data.data.items || []);
        setModulos(permissoesRes.data.data || []);

        if (isEdit) {
          const [usuarioRes, permissoesUsuarioRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API}/usuario/${id}`),
            axios.get(`${process.env.REACT_APP_API}/permissoes/usuario/${id}`),
          ]);

          if (cancelled) return;

          const data = usuarioRes.data.data;
          const permissoesData = permissoesUsuarioRes.data.data;

          // Permissões extras do usuário (origem = "Extra")
          const extrasIds = permissoesData.permissoes
            ?.filter((p) => p.origem === "Extra")
            .map((p) => p.id) || [];

          // Permissões do grupo (origem = "Grupo")
          const grupoPermissoes = permissoesData.permissoes
            ?.filter((p) => p.origem === "Grupo")
            .map((p) => p.id) || [];

          setPermissoesGrupo(grupoPermissoes);

          setForm({
            nome: data.nome || "",
            email: data.email || "",
            tipoUsuario: data.tipoUsuario || 3,
            grupoId: data.grupoId || data.grupo?.id || "",
            permissoesExtrasIds: extrasIds,
          });
        }
      } catch (err) {
        console.error(err);
        AlertComponent("Erro", "Erro ao carregar dados", { icon: "error" });
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    }

    carregar();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  // Quando muda o grupo, carrega as permissões do grupo selecionado
  useEffect(() => {
    if (!form.grupoId) {
      setPermissoesGrupo([]);
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API}/grupos/${form.grupoId}`)
      .then((res) => {
        const permissoes = res.data.data?.permissoes || [];
        setPermissoesGrupo(permissoes.map((p) => p.id || p.permissaoId));
      })
      .catch((err) => console.error(err));
  }, [form.grupoId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "tipoUsuario" || name === "grupoId"
          ? value === ""
            ? ""
            : parseInt(value)
          : value,
    }));
  };

  const togglePermissaoExtra = (permissaoId) => {
    setForm((prev) => {
      const existe = prev.permissoesExtrasIds.includes(permissaoId);
      return {
        ...prev,
        permissoesExtrasIds: existe
          ? prev.permissoesExtrasIds.filter((id) => id !== permissaoId)
          : [...prev.permissoesExtrasIds, permissaoId],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.grupoId) {
      AlertComponent("Atenção", "Selecione um grupo para o usuário", {
        icon: "warning",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        nome: form.nome,
        email: form.email,
        tipoUsuario: form.tipoUsuario,
        grupoId: form.grupoId,
        permissoesExtrasIds: form.permissoesExtrasIds,
      };

      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API}/usuario/${id}`, payload);
        AlertComponent("Sucesso", "Usuário salvo com sucesso!", {
          icon: "success",
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API}/usuario`, payload);
        AlertComponent("Sucesso", "Usuário criado com sucesso!", {
          icon: "success",
        });
      }
      navigate(`/usuarios?filtro=${encodeURIComponent(form.nome)}`);
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);
      AlertComponent(
        "Erro",
        error?.response?.data?.message ?? "Erro ao salvar usuário",
        { icon: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const getTiposUsuarioDisponiveis = () => {
    if (isSistema) {
      return [{ value: 1, label: "Sistema" }];
    }
    return [
      { value: 2, label: "Administrador" },
      { value: 3, label: "Usuário" },
    ];
  };



  if (loadingData) return <Loading />;

  return (
    <div className="w-full">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Usuário" : "Novo Usuário"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit
            ? "Atualize as informações do usuário"
            : "Preencha as informações para criar um novo usuário"}
        </p>
      </div>

      {/* ABAS */}
      <div className="mb-8 border-b">
        <nav className="flex gap-6">
          {[
            { id: "dados", label: "Dados do Usuário" },
            { id: "permissoes", label: "Permissões Extras" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setAba(tab.id)}
              className={`pb-3 text-sm font-medium transition ${
                aba === tab.id
                  ? "border-b-2 border-slate-900 text-slate-900"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {tab.label}
              {tab.id === "permissoes" && form.permissoesExtrasIds.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {form.permissoesExtrasIds.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ABA DADOS */}
      {aba === "dados" && (
        <div className="bg-white p-8 rounded-xl shadow border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome completo
              </label>
              <input
                type="text"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="Digite o nome completo"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-mail
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="usuario@exemplo.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Usuário
              </label>
              <select
                name="tipoUsuario"
                value={form.tipoUsuario}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                disabled={
                  loading ||
                  (isSistema && getTiposUsuarioDisponiveis().length === 1)
                }
              >
                {getTiposUsuarioDisponiveis().map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grupo
              </label>
              <select
                name="grupoId"
                value={form.grupoId}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                disabled={loading}
              >
                <option value="">Selecione um grupo</option>
                {grupos.map((grupo) => (
                  <option key={grupo.id} value={grupo.id}>
                    {grupo.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ABA PERMISSÕES EXTRAS */}
      {aba === "permissoes" && (
        <div className="bg-white p-8 rounded-xl shadow border">
          <p className="text-sm text-gray-500 mb-6">
            Selecione permissões adicionais além das herdadas do grupo.
            Permissões já incluídas no grupo aparecem desabilitadas.
          </p>

          {modulos.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              Nenhuma permissão disponível
            </p>
          ) : (
            <div className="space-y-4">
              {modulos.map((modulo) => {
                const perms = modulo.permissoes || [];
                
                const todasDoGrupo = perms.every((p) =>
                  permissoesGrupo.includes(p.id)
                );
                const algumaDoGrupo = perms.some((p) =>
                  permissoesGrupo.includes(p.id)
                );
                const algumaExtra = perms.some((p) =>
                  form.permissoesExtrasIds.includes(p.id)
                );
                const countSelecionadas = perms.filter(
                  (p) =>
                    permissoesGrupo.includes(p.id) ||
                    form.permissoesExtrasIds.includes(p.id)
                ).length;

                return (
                  <div
                    key={modulo.id}
                    className={`border rounded-lg overflow-hidden ${
                      todasDoGrupo
                        ? "border-emerald-200"
                        : algumaDoGrupo || algumaExtra
                        ? "border-blue-200"
                        : ""
                    }`}
                  >
                    {/* Header do módulo */}
                    <div
                      className={`px-4 py-3 font-medium text-sm flex items-center justify-between ${
                        todasDoGrupo
                          ? "bg-emerald-50 text-emerald-800"
                          : algumaDoGrupo || algumaExtra
                          ? "bg-blue-50 text-blue-800"
                          : "bg-gray-50 text-gray-700"
                      }`}
                    >
                      <span>{modulo.nome}</span>
                      <span className="text-xs font-normal">
                        {countSelecionadas}/{perms.length}
                      </span>
                    </div>

                    {/* Permissões do módulo */}
                    <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                      {perms.map((perm) => {
                        const isFromGrupo = permissoesGrupo.includes(perm.id);
                        const isExtra = form.permissoesExtrasIds.includes(perm.id);

                        // Extrai o nome curto (ex: "Visualizar" de "Visualizar Usuários")
                        const nomeExibicao = perm.nome
                          .replace(modulo.nome, "")
                          .replace("Usuários", "")
                          .replace("Acessos", "")
                          .replace("Exceções", "")
                          .replace("Meu Perfil", "")
                          .replace("Minha Senha", "Senha")
                          .trim() || perm.nome;

                        return (
                          <label
                            key={perm.id}
                            className={`flex items-center gap-2 p-2 rounded text-sm cursor-pointer transition ${
                              isFromGrupo
                                ? "bg-emerald-50 text-emerald-700 cursor-not-allowed"
                                : isExtra
                                ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                                : "hover:bg-slate-50 text-slate-600"
                            }`}
                            title={perm.nome}
                          >
                            <input
                              type="checkbox"
                              checked={isFromGrupo || isExtra}
                              onChange={() =>
                                !isFromGrupo && togglePermissaoExtra(perm.id)
                              }
                              disabled={loading || isFromGrupo}
                              className="w-4 h-4 flex-shrink-0"
                            />
                            <span className="truncate">{nomeExibicao}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 pt-4 border-t flex gap-6 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span>
              Via grupo
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span>
              Permissão extra
            </div>
          </div>
        </div>
      )}

      {/* AÇÕES */}
      <div className="mt-10 flex justify-end gap-3 border-t pt-6">
        <button
          type="button"
          onClick={() => navigate("/usuarios")}
          className="px-5 py-2 rounded-md border hover:bg-gray-50 disabled:opacity-50"
          disabled={loading}
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar usuário"}
        </button>
      </div>
    </div>
  );
}